<template>
  <div class="stamp-duty-confirm-transfer">
    <div class="column">
      <b-field
        :label="`${$t('bought_value')}（${$t('mop')}） :`"
        :message="$t(validation.firstError('boughtValue'))"
      >
        <p class="control">
          <b-button>MOP</b-button>
        </p>
        <b-input
          v-model="boughtValue"
          :placeholder="`${$t('please_input_int_amt')}`"
        />
      </b-field>
    </div>

    <div class="column">
      <b-field
        :label="`${$t('house_record_value')}（${$t('mop')}） :`"
        :message="$t(validation.firstError('houseRecordValue'))"
      >
        <p class="control">
          <b-button>MOP</b-button>
        </p>
        <b-input
          v-model="houseRecordValue"
          :placeholder="`${$t('please_input_int_amt')}`"
        />
      </b-field>
    </div>

    <div v-if="isShowResult" class="column">
      <b-table
        :data="this.tableData"
        :columns="columns"
        :hoverable="true"
        :striped="true"
      >
        <template #footer>
          <div class="columns is-mobile">
            <div class="column is-8 has-text-right">
              {{ $t("property_transfer_stamp_duty") }} :
              <br />
              {{ $t("voucher_stamp_duty") }} :
              <br />
              {{ $t("total_tax_amt") }} :
            </div>

            <div class="column is-4 has-text-right">
              ${{ transferStampDuty | formatNumberWithoutPoint }}
              <br />
              ${{ voucher_stamp_duty | formatNumberWithoutPoint }}
              <br />
              ${{ totalTaxAmt | formatNumberWithoutPoint }}
            </div>
          </div>
        </template>
      </b-table>
    </div>
    <div v-if="isShowResult" class="column is-full">
      1) {{ $t("remark_1") }}
    </div>

    <div v-if="isShowResult" class="column is-full">
      2) {{ $t("remark_2") }}
    </div>

    <div class="column">
      <b-button
        @click="calculate"
        class="is-primary"
        style="background-color: #00775e"
      >
        {{ $t("calculate") }}
      </b-button>
    </div>
  </div>
</template>

<script>
export default {
  data() {
    return {
      boughtValue: "",
      houseRecordValue: "",
      tableData: "",
      isShowResult: false,
      transferStampDuty: null,
      voucher_stamp_duty: null,
      totalTaxAmt: null,
      columns: [
        {
          field: "from",
          label: this.$t("from"),
          numeric: true,
        },
        {
          field: "till",
          label: this.$t("till"),
          numeric: true,
        },
        {
          field: "tax_rate",
          label: this.$t("tax_rate"),
          centered: true,
        },
        {
          field: "tax_amt",
          label: this.$t("tax_amt"),
          numeric: true,
        },
        {
          field: "level_tax",
          label: this.$t("level_tax"),
          numeric: true,
        },
        {
          field: "sub_tax_amt",
          label: this.$t("sub_tax_amt"),
          numeric: true,
        },
      ],
    };
  },

  validators: {
    boughtValue: function(value) {
      return this.$validator()
        .value(value)
        .required()
        .integer();
    },

    houseRecordValue: function(value) {
      return this.$validator()
        .value(value)
        .integer();
    },
  },

  computed: {
    propertyValue() {
      const boughtValueInt = parseInt(this.boughtValue);

      const houseRecordValueInt = this.houseRecordValue
        ? parseInt(this.houseRecordValue)
        : 0;

      if (boughtValueInt > houseRecordValueInt) {
        return this.boughtValue;
      }

      return this.houseRecordValue;
    },
  },

  methods: {
    calculate() {
      let results = [
        {
          from: this.$options.filters.formatNumberWithoutPoint(0),
          till: this.$options.filters.formatNumberWithoutPoint(2000000),
          tax_rate: "0.5%",
        },
        {
          from: this.$options.filters.formatNumberWithoutPoint(2000001),
          till: this.$options.filters.formatNumberWithoutPoint(4000000),
          tax_rate: "1.5%",
        },
        {
          from: "",
          till: `${this.$t(
            "ahead_1"
          )}${this.$options.filters.formatNumberWithoutPoint(4000000)}${this.$t(
            "ahead_2"
          )}`,
          tax_rate: "2.5%",
        },
      ];

      const calculateTaxAmt = this.calculateTaxAmt().map((result) => {
        return this.$options.filters.formatNumber(result);
      });

      const levelTax = this.levelTax().map((result) => {
        return this.$options.filters.formatNumberWithoutPoint(result);
      });

      const subTaxAmt = this.subTaxAmt().map((result) => {
        return this.$options.filters.formatNumberWithoutPoint(result);
      });

      this.propertyTransferStampDuty();
      this.voucherStampDuty();

      this.totalTaxAmt = this.transferStampDuty + this.voucher_stamp_duty;

      results.map(function(result, index) {
        result.tax_amt = calculateTaxAmt[index];
        result.level_tax = levelTax[index];
        result.sub_tax_amt = subTaxAmt[index];
      });

      this.tableData = results;

      this.$validate().then((success) => {
        if (success) {
          this.isShowResult = true;
        }
      });
    },

    calculateTaxAmt() {
      let tax_amt_level1 = 0;
      let tax_amt_level2 = 0;
      let tax_amt_level3 = 0;
      let value = this.propertyValue;

      if (value > 4000000) {
        tax_amt_level3 = value - 4000000;

        value = value - tax_amt_level3;
      }

      if (2000000 < value && value <= 4000000) {
        tax_amt_level2 = value - 2000000;

        value = value - tax_amt_level2;
      }

      if (0 < value && value <= 2000000) {
        tax_amt_level1 = value;
      }

      return [tax_amt_level1, tax_amt_level2, tax_amt_level3];
    },

    levelTax() {
      const result = [];
      const tax_rate = [0.005, 0.015, 0.025];
      const taxAmtS = this.calculateTaxAmt();

      taxAmtS.forEach(function(taxAmt, index) {
        result.push(Math.round(taxAmt * tax_rate[index]));
      });

      return result;
    },

    subTaxAmt() {
      const levelTaxes = this.levelTax();
      const result = [];

      levelTaxes.forEach(function(levelTax, index) {
        if (levelTax > 0) {
          let subTotal = 0;
          for (let i = 0; i <= index; i++) {
            subTotal = subTotal + levelTaxes[i];
          }
          result.push(subTotal);
        } else {
          result.push(0);
        }
      });

      return result;
    },

    propertyTransferStampDuty() {
      const subTaxAmt = this.subTaxAmt();
      this.transferStampDuty = Math.max(...subTaxAmt);
    },

    voucherStampDuty() {
      this.voucher_stamp_duty = Math.round(this.transferStampDuty * 0.05);
    },
  },
};
</script>
